import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMenuPermission } from '../../hooks/useMenuPermission';
import { useVisibleTabs } from '../../hooks/useVisibleTabs';
import { getCrudPermissionKey } from '../../utils/actionPermission';
import { roleAllowsAction } from '../../utils/roleAllowsAction';
import { isRuleActionBlocked, ruleActionDisabledReason, actionDoneState } from '../../utils/actionRuleEngine';
import { Button, Card, Dropdown, Tag, Tooltip, message } from 'antd';
import {
  AimOutlined,
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  IdcardOutlined,
  LaptopOutlined,
  LinkOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  ProjectOutlined,
  ShareAltOutlined,
  SolutionOutlined,
  SyncOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { getFormGroups } from '../../services/dropdownApi';
import { getModuleActions } from '../../services/detailedViewApi';
import { getModuleDocuments } from '../../services/documentApi';
import { clearAuthTokenOverride, fetchJsonWithAuth, setAuthTokenOverride } from '../../services/authApi';
import { AUTH_URL } from '../../services/apiConfig';
import { loadDetailDefaults } from '../../services/detailDefaults';
import { extractDocuments } from './renderConfig';
import { buildHeaderFromGroups, getModuleConfig } from './detailConfig/headerConfig';
import { resolveMetaItem } from './headerMeta';
import { auxRecordsSignature, makeAuxAccessor, normalizeAuxRecords } from './auxRecords';
import AppButton from '../AppButton';
import { FinanceStatusPill, FinanceWorkflowSteps } from '../finance/FinanceComponents';
import { FinanceAvatar } from '../finance/FinanceRecordsTable';
import { getWorkflowSteps } from '../finance/financeUtils';
import '../finance/finance-components.css';
import DocumentViewer from '../DocumentViewer';
import '../../styles/detail-header-card.css';

// String icon keys (from headerConfig) → icon elements.
const ICONS = {
  location: <EnvironmentOutlined />,
  designation: <SolutionOutlined />,
  experience: <ClockCircleOutlined />,
  employment: <LaptopOutlined />,
  rate: <DollarOutlined />,
  source: <AimOutlined />,
  resume: <FileTextOutlined />,
  edit: <EditOutlined />,
  approve: <CheckCircleOutlined />,
  reject: <CloseCircleOutlined />,
  // "Open in / external" icons for actions like Quick Submit's "Open JD".
  // `export` matches the box-with-out-arrow look; the others are aliases so an
  // admin-chosen icon key renders regardless of which of these they picked.
  export: <ExportOutlined />,
  openJd: <ExportOutlined />,
  link: <LinkOutlined />,
  shareAlt: <ShareAltOutlined />,
  // Finance header chip icons (referenced by a finance module's header.chips)
  idcard: <IdcardOutlined />,
  bank: <BankOutlined />,
  team: <TeamOutlined />,
  project: <ProjectOutlined />,
  cycle: <SyncOutlined />,
  period: <CalendarOutlined />,
  // Finance action icons — keyed by the antd component name stored on the
  // seeded rowActionConfig entries, so config-driven finance buttons keep their
  // icons (Process / Mark Paid / Generate Invoice / Generate Payslip).
  PlayCircleOutlined: <PlayCircleOutlined />,
  DollarOutlined: <DollarOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  FileDoneOutlined: <FileDoneOutlined />,
};

// A single finance meta chip (icon + optional label + value + optional badge).
// Rendered by the `variant: 'finance'` header below — no separate finance-header
// component; the design is assembled here from the shared finance primitives.
function MetaChip({ chip }) {
  const icon = chip.icon ? <span className="fin-meta-chip__icon">{chip.icon}</span> : null;
  return (
    <span className="fin-meta-chip">
      {chip.tooltip && icon ? <Tooltip title={chip.tooltip}>{icon}</Tooltip> : icon}
      {chip.label ? <span className="fin-meta-chip__label">{chip.label}</span> : null}
      <span className="fin-meta-chip__value">{chip.value ?? '—'}</span>
      {chip.badge ? <span className="fin-badge fin-badge--brand">{chip.badge}</span> : null}
    </span>
  );
}

// Build a flat record from the fetched form-groups so route templates such as
// "/app/candidates/edit/:_id" can be filled with the record's real values.
function actionRecordValue(value) {
  if (Array.isArray(value)) return value.map(actionRecordValue);
  if (value && typeof value === 'object' && 'id' in value && 'value' in value) {
    return value.id ?? value.value;
  }
  return value;
}

function recordFromGroups(formGroups, id) {
  const record = { _id: id, id };
  // recordMeta (ridden along on the groups array by getFormGroups) carries the
  // record's audit stamps and scalar linkage ids (jobId/applicantId/…) that are
  // never form-group fields — merged FIRST so route templates like
  // "/submission/edit/:_id?jobId=:jobId" resolve, while real form fields below
  // still win on any key collision.
  const meta = formGroups?.recordMeta;
  if (meta && typeof meta === 'object') {
    for (const [key, value] of Object.entries(meta)) {
      if (!(key in record)) record[key] = value;
    }
  }
  for (const group of formGroups ?? []) {
    for (const field of group.fields ?? []) {
      if (field?.field != null && field.value !== undefined) {
        // Detail shaping turns lookups into { id, value }. Action rules and
        // route templates must continue comparing/using the stored id, just as
        // they did before the header opted into detail-view shaping.
        record[field.field] = actionRecordValue(field.value);
      }
    }
  }
  return record;
}

function templatePart(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'object') return String(value.id ?? value.value ?? '');
  return String(value);
}

// Fill ":_id", ":field" and "{field}" placeholders in a route template.
function fillRouteTemplate(template, record) {
  return String(template)
    .replace(/\{([^}]+)\}/g, (_, key) => encodeURIComponent(templatePart(record?.[key])))
    .replace(/:([A-Za-z_][\w]*)/g, (_, key) => encodeURIComponent(templatePart(record?.[key])));
}

function getNestedValue(record, path) {
  return String(path || '').split('.').reduce((value, key) => value?.[key], record);
}

function comparable(value) {
  return String(value ?? '').trim().toLowerCase();
}

// Rule evaluation (conditionMatches/isActionBlocked) lives in
// ../../utils/actionRuleEngine, shared with ListView.jsx. Detail pages have no
// VIEW scope (no myJobs/allJobs tabs), but a module's detail config may derive
// a per-RECORD scope via `resolveScope(ctx)` (e.g. "is this job assigned to
// me → myJobs, else allJobs") so the same admin scope rules that gate the List
// also gate the Detail header. Without a resolver, scope stays `undefined` and
// scope rules simply can't match — exactly the old behavior.
function isBlocked(action, record, rules, scope) {
  return isRuleActionBlocked(action.key, record, rules, scope);
}

// actionDoneState now lives in utils/actionRuleEngine (shared with ListView and
// the row-action dropdown), so the "done" terminus renders identically on every
// surface.

// Admin-configured Module Action Rules can mark an action "disabled" instead
// of "blocked" (see ruleActionDisabledReason): the button stays visible in its
// normal spot with its normal label, just greyed out with a tooltip — used
// e.g. for Approve/Reject while a submission sits in "Resubmission", so the
// reviewer can see the actions exist and understand why they're not usable
// yet, instead of them silently disappearing.
function actionDisabledReason(action, record, rules, scope) {
  return ruleActionDisabledReason(action.key, record, rules, scope);
}

function normalizeConfiguredAction(action) {
  const key = String(action.key ?? '');
  return {
    ...action,
    key,
    label: action.label || key,
    route: action.route || action.routeTemplate || (
      action.actionType === 'modal' && (action.popupKey || key)
        ? `/popup/${action.popupKey || key}`
        : ''
    ),
  };
}

// Resolve document objects from the form-groups for the DocumentViewer.
// `fieldName` is the preferred field key (e.g. 'resume'); falls back to any
// resume/CV-looking field label or key.
function resolveDocuments(formGroups, fieldName) {
  const fields = (formGroups ?? []).flatMap((group) => group.fields ?? []);
  const mayUseResumeFallback = !fieldName || /resume|cv/i.test(fieldName);
  const target =
    fields.find((field) => field.field === fieldName) ??
    (mayUseResumeFallback
      ? fields.find((field) => /resume|cv/i.test(`${field.field ?? ''} ${field.label ?? ''}`))
      : undefined);
  if (!target) return [];

  const targetType = String(target.type ?? '').trim().toLowerCase();
  const isFileField = targetType === 'file' || targetType === 'document' || targetType === 'upload';
  if (!isFileField && typeof target.value === 'string' && target.value.trim()) {
    const richText = targetType === 'text-editor' || /<[^>]+>/.test(target.value);
    return [{
      name: target.label || fieldName || 'Document',
      type: richText ? 'html' : 'text',
      content: target.value,
    }];
  }

  const fileDocuments = extractDocuments(target.value).filter((document) => document.url);
  if (fileDocuments.length) return fileDocuments;

  // A document action may point at inline content instead of an uploaded file
  // (for example a rich-text jobDescription). Preserve that content as an
  // in-memory viewer document; DocumentViewer sanitizes HTML before rendering.
  if (typeof target.value === 'string' && target.value.trim()) {
    const richText = targetType === 'text-editor' || /<[^>]+>/.test(target.value);
    return [{
      name: target.label || fieldName || 'Document',
      type: richText ? 'html' : 'text',
      content: target.value,
    }];
  }

  return [];
}

/*
 * Dynamic detail-view header. Self-contained: given `moduleName` + `id` it
 * fetches its own form-groups and builds the header object via
 * headerConfig.buildHeaderFromGroups — the page only passes moduleName + id.
 *
 *  moduleName, id  : forwarded back to onAction / onTabChange
 *  customConfigName : optional config key used only for rendering the card
 *  auxRecords      : optional { key: { module, id } } map of EXTRA records to
 *                    resolve alongside the primary one, exposed to the header
 *                    config as `aux(key)(field)` — see ./auxRecords.js for why
 *                    this is a host prop rather than config-declared
 *  onAction(key, { moduleName, id })  : button + more-menu clicks
 *                                       (custom parent-level handling)
 *  onTabChange(key, { moduleName, id }) : nav tab clicks
 *
 * Route-based actions (action.route defined in module config) are navigated
 * directly here so the click → navigate path is as short as possible.
 */
export default function DetailHeaderCard({
  moduleName,
  id,
  customConfigName,
  auxRecords,
  authToken,
  onAction,
  onTabChange,
  onHeaderData,
  hideActions = false,
  defaultTab,
  refreshKey = 0,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [formGroups, setFormGroups] = useState([]);
  const [configuredActions, setConfiguredActions] = useState([]);
  const [actionRules, setActionRules] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDocs, setViewerDocs] = useState([]);
  // Bumped after a same-page 'api' action (Process / Mark Paid / Generate
  // Payslip / …) succeeds, so the fetch effect below re-runs and pulls the
  // record's new status/chips/done-state without a full page reload. Route-
  // based actions don't need this: they either navigate away or go through
  // DynamicPopupRoute, which already refreshes via `refreshKey`.
  const [actionRefreshTick, setActionRefreshTick] = useState(0);
  const configName = customConfigName || moduleName;

  useEffect(() => {
    if (authToken) setAuthTokenOverride(authToken);
    return () => {
      if (authToken) clearAuthTokenOverride();
    };
  }, [authToken]);

  useEffect(() => {
    if (!moduleName) return undefined;

    let cancelled = false;

    async function fetchFormGroups() {
      try {
        // loadDetailDefaults() (cached) ensures resume/document URLs resolve
        // against the admin-configured S3 base, same as DetailViewV1.
        // Actions are fetched by configName (customConfigName, when given) so a
        // context-specific card — e.g. the Quick Submit page's "job" module
        // header rendered under customConfigName="submissionJob" — can carry
        // its own action set instead of always sharing the base module's
        // (e.g. the real Job detail page's) actions. Falls back to moduleName
        // when no customConfigName is passed, so every existing page keeps
        // its current behavior unchanged.
        const [data, actionConfig] = await Promise.all([
          getFormGroups({ module: moduleName, id, view: 'detail' }),
          getModuleActions(configName).catch(() => ({ actions: [], actionRules: [] })),
          loadDetailDefaults(),
        ]);
        if (!cancelled) {
          setFormGroups(Array.isArray(data) ? data : []);
          setConfiguredActions(actionConfig.actions ?? []);
          setActionRules(actionConfig.actionRules ?? []);
        }
      } catch (err) {
        console.error(`Failed to fetch ${moduleName} header fields:`, err);
        if (!cancelled) {
          setFormGroups([]);
          setConfiguredActions([]);
          setActionRules([]);
        }
      }
    }

    fetchFormGroups();

    return () => { cancelled = true; };
  }, [moduleName, configName, id, refreshKey, actionRefreshTick]);

  // ── Auxiliary records ──────────────────────────────────────────────────────
  // Extra records the host declared via `auxRecords`, fetched with the SAME api
  // as the primary record (getFormGroups + detail shaping) so an aux value is
  // exactly what the detail view would show. Every request fails open to `[]`:
  // a header must never blank out or throw because a secondary record 404'd,
  // errored, or simply has no id yet. `auxSignature` (not the prop identity)
  // drives the effect, since hosts pass an inline object literal.
  // The fetched payload is stored WITH the signature it was fetched for, so a
  // changed/cleared `auxRecords` invalidates it by comparison instead of by a
  // synchronous setState in the effect body (which would cascade a render).
  const [auxState, setAuxState] = useState({ signature: '', groups: {} });
  const auxSignature = auxRecordsSignature(auxRecords);
  useEffect(() => {
    const requests = normalizeAuxRecords(auxRecords);
    if (!requests.length) return undefined;
    let cancelled = false;
    Promise.all(requests.map(async (request) => {
      const data = await getFormGroups({ module: request.module, id: request.id, view: 'detail' })
        .catch(() => []);
      return [request.key, Array.isArray(data) ? data : []];
    }))
      .then((entries) => {
        if (!cancelled) setAuxState({ signature: auxSignature, groups: Object.fromEntries(entries) });
      })
      // fail open: no aux values, header renders exactly as it would without them
      .catch(() => { if (!cancelled) setAuxState({ signature: auxSignature, groups: {} }); });
    return () => { cancelled = true; };
  }, [auxSignature, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const aux = useMemo(
    () => makeAuxAccessor(auxState.signature === auxSignature ? auxState.groups : {}),
    [auxState, auxSignature],
  );

  const header = useMemo(
    () => buildHeaderFromGroups(configName, formGroups, { aux }),
    [configName, formGroups, aux],
  );

  useEffect(() => {
    if (header && onHeaderData) onHeaderData(header);
  }, [header]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    eyebrow,
    title = '-',
    status,
    titleIds = [],
    meta = [],
    ids = [],
    tabs = [],
    stats = [],
    timestamp = null,
  } = header ?? {};

  const { canAdd, canEdit, canDelete } = useMenuPermission(moduleName);

  const record = useMemo(() => recordFromGroups(formGroups, id), [formGroups, id]);

  // Config-driven per-tab visibility (e.g. hide "Candidates" on a job that isn't
  // assigned to the current user). A tab with no `visibleWhen` predicate always
  // shows, so this changes nothing for existing modules/tabs.
  const visibleTabs = useVisibleTabs(tabs, { id, moduleName, record });

  // ── Per-record scope for admin action rules ─────────────────────────────
  // The module's detail config may declare `resolveScope(ctx)` (sync or async)
  // returning the scope string the admin's rules compare against (e.g.
  // 'myJobs'/'allJobs' from "is this record assigned to me"). The SAME rules
  // that gate the List rows then gate the Detail header — nothing hardcoded
  // here. While a declared resolver is still pending, rule-gated actions stay
  // hidden so a restricted action never flashes before disappearing; a module
  // without a resolver keeps today's behavior (scope undefined, scope rules
  // never match).
  const scopeResolver = getModuleConfig(configName)?.resolveScope;
  const [recordScope, setRecordScope] = useState(undefined);
  useEffect(() => {
    if (typeof scopeResolver !== 'function' || !id) return undefined;
    let cancelled = false;
    const currentUserId = Number(localStorage.getItem('userId')) || 0;
    Promise.resolve()
      .then(() => scopeResolver({ id, moduleName, record, currentUserId }))
      .then((scope) => { if (!cancelled) setRecordScope(scope ?? null); })
      .catch(() => { if (!cancelled) setRecordScope(null); }); // fail open: no scope
    return () => { cancelled = true; };
    // record is derived from formGroups; keying on formGroups avoids re-running
    // on every render while still re-resolving once the record data arrives.
  }, [scopeResolver, id, moduleName, formGroups]); // eslint-disable-line react-hooks/exhaustive-deps
  const scopeReady = typeof scopeResolver !== 'function' || recordScope !== undefined;
  const detailActions = useMemo(() => {
    if (!scopeReady) return []; // resolver declared but still pending — no flash
    return configuredActions
      .filter((action) => action.showInDetail ?? action.showInList)
      .map(normalizeConfiguredAction)
      .filter((action) => !isBlocked(action, record, actionRules, recordScope ?? undefined));
  }, [actionRules, configuredActions, record, recordScope, scopeReady]);

  function isActionAllowed(action) {
    const pk = getCrudPermissionKey(action);
    if (pk === 'add'    && !canAdd)    return false;
    if (pk === 'edit'   && !canEdit)   return false;
    if (pk === 'delete' && !canDelete) return false;
    if (!roleAllowsAction(action.permission, configName)) return false;
    if (action.permissionKey) {
      const rowPermission = getNestedValue(record, `actionPermission.${action.permissionKey}`);
      if (rowPermission === false || rowPermission === 0 || rowPermission === '0') return false;
    }
    return true;
  }

  // Own the active tab locally so a click highlights immediately — waiting on
  // defaultTab to round-trip back through the parent (URL state, re-fetches)
  // let the highlight snap back to the first tab while content had already
  // switched underneath it.
  const [tabSelection, setTabSelection] = useState({
    sourceDefaultTab: defaultTab,
    selectedTab: defaultTab,
  });
  const localActiveTab = tabSelection.sourceDefaultTab === defaultTab
    ? tabSelection.selectedTab
    : defaultTab;
  const activeTab = visibleTabs.some((tab) => tab.key === localActiveTab)
    ? localActiveTab
    : visibleTabs[0]?.key;

  // Admin can restrict an action to selected Detail tabs with allowedTabs.
  const isAllowedOnTab = (action) => !action.allowedTabs?.length
    || action.allowedTabs.some((tab) => comparable(tab) === comparable(activeTab));
  const visiblePrimaryActions = detailActions.filter(
    (action) => action.detailPlacement === 'primary' && isActionAllowed(action) && isAllowedOnTab(action),
  );
  const visibleMoreActions = detailActions.filter(
    (action) => action.detailPlacement !== 'primary' && isActionAllowed(action) && isAllowedOnTab(action),
  );

  if (!header) return null;

  const fireAction = (key) => {
    const action = [...visiblePrimaryActions, ...visibleMoreActions].find((a) => a.key === key);

    // Open a document (e.g. resume) in the DocumentViewer modal.
    if (action?.actionType === 'document' && action.documentField) {
      const docs = resolveDocuments(formGroups, action.documentField);
      if (docs.length) {
        setViewerDocs(docs);
        setViewerOpen(true);
        return;
      }
      // Fallback: the file may live in the central documents registry, and
      // possibly under a DIFFERENT module/record than the one on screen — e.g. a
      // submission's resume is uploaded against the CANDIDATE (module
      // "candidates", refId = the submission's applicantId). Admin config points
      // at it: documentModule (source module) + documentIdField (the record key
      // holding that module's id). Defaults to the current module + id.
      const docModule = action.documentModule || moduleName;
      const docId = action.documentIdField
        ? getNestedValue(record, action.documentIdField)
        : id;
      getModuleDocuments(docModule, docId, action.documentField)
        .then((res) => {
          const central = (res?.data ?? [])
            .map((d) => ({
              name: d.fileName || d.name || d.uploadedFileName || action.documentField,
              url: d.fileUrl || d.url || '',
              type: 'file',
            }))
            .filter((d) => d.url);
          if (central.length) {
            setViewerDocs(central);
            setViewerOpen(true);
          } else {
            message.warning(`No ${(action.label || 'document').toLowerCase()} available`);
          }
        })
        .catch(() => message.warning(`No ${(action.label || 'document').toLowerCase()} available`));
      return;
    }

    if (action?.actionType === 'copy') {
      const value = getNestedValue(record, action.copyField || 'id');
      navigator.clipboard.writeText(templatePart(value))
        .then(() => message.success('Copied to clipboard'))
        .catch(() => message.error('Could not copy to clipboard'));
      return;
    }

    if (action?.actionType === 'api' && action.apiPath) {
      const path = fillRouteTemplate(action.apiPath, record);
      const method = String(action.apiMethod || 'POST').toUpperCase();
      fetchJsonWithAuth(AUTH_URL, path.startsWith('/') ? path : `/${path}`, {
        method,
        body: ['GET', 'DELETE'].includes(method) ? undefined : JSON.stringify({ ids: [id], record }),
      })
        .then(() => {
          message.success(`${action.label} completed`);
          // The record just changed on the server (status, workflow step,
          // computed fields) — refetch this header's own data so the status
          // pill / chips / done-state buttons update immediately, and let the
          // parent page know in case it holds its own copy of the record
          // (e.g. FinanceDetailPage's KPI cards / overview tab).
          setActionRefreshTick((tick) => tick + 1);
          onAction?.(key, { moduleName, id, action, refreshed: true });
        })
        .catch((error) => message.error(error.message || `${action.label} failed`));
      return;
    }

    if (action?.route) {
      const resolvedRoute = typeof action.route === 'function'
        ? action.route(id)
        : fillRouteTemplate(action.route, recordFromGroups(formGroups, id));

      // Admin-configured "open in new tab" (e.g. Quick Submit's "Open JD",
      // so reviewing the job description never loses the in-progress form).
      if (action.linkTarget === '_blank') {
        window.open(resolvedRoute, '_blank', 'noopener');
        return;
      }

      if (resolvedRoute.startsWith('/popup/')) {
        navigate(resolvedRoute, {
          state: {
            background: location,
            moduleName,
            selectedIds: [id],
            selectedRecords: [record],
            popupContext: action.popupState ?? {},
            popupAction: { key: action.key, apiMethod: action.apiMethod, apiPath: action.apiPath, defaultTemplateId: action.defaultTemplateId },
            ...action.popupState,
          },
        });
      } else {
        navigate(resolvedRoute);
      }
      return;
    }

    // No route — bubble up to parent for custom handling.
    onAction?.(key, { moduleName, id, action });
  };

  const selectTab = (key) => {
    setTabSelection({ sourceDefaultTab: defaultTab, selectedTab: key });
    onTabChange?.(key, { moduleName, id });
  };

  // Each item carries its own onClick so we don't rely on the global menu.onClick.
  const moreMenuItems = visibleMoreActions.map((action) => {
    const done = actionDoneState(action, record, recordScope ?? undefined);
    const disabledReason = !done && actionDisabledReason(action, record, actionRules, recordScope ?? undefined);
    return {
      key: action.key,
      label: done ? done.label : action.label,
      danger: action.danger,
      disabled: !!done || !!disabledReason,
      title: disabledReason || undefined,
      onClick: done || disabledReason ? undefined : () => fireAction(action.key),
    };
  });

  // ── Finance variant ────────────────────────────────────────────────────────
  // A module whose detailConfig declares header.variant === 'finance' renders
  // the finance header design (avatar + icon chips + engagement badge +
  // state-gated buttons + workflow stepper) INLINE here — assembled from the
  // shared finance primitives (FinanceAvatar / FinanceStatusPill /
  // FinanceWorkflowSteps), NOT a separate finance-header component. It stays
  // fully config- and admin-driven: fields from Form Groups, buttons from Row
  // Actions, gating from Module Action Rules. Opt-in per module, so every other
  // module is completely unaffected.
  const financeHeader = getModuleConfig(configName)?.header;
  if (financeHeader?.variant === 'finance') {
    const displayText = (value) => {
      if (value == null) return '';
      if (Array.isArray(value)) return value.map(displayText).filter(Boolean).join(', ');
      // Lookups are shaped { id, value } — show the display name, not the id
      // (unlike the action `record`, which flattens lookups to their id).
      if (typeof value === 'object') return String(value.value ?? value.label ?? value.name ?? value.id ?? '');
      return String(value);
    };
    // Field key → raw form-group value (dotted keys like "billing.status" and
    // "period.label" are stored as literal keys), preserving { id, value }.
    const fieldValues = {};
    for (const group of formGroups ?? []) {
      for (const field of group.fields ?? []) {
        if (field?.field != null) fieldValues[field.field] = field.value;
      }
    }
    const readField = (key) => displayText(fieldValues[key]);

    const rawStatus = readField(financeHeader.statusField);
    const financeChips = (financeHeader.chips ?? []).map((chip) => ({
      key: chip.field,
      icon: chip.icon ? ICONS[chip.icon] : undefined,
      label: chip.label,
      value: readField(chip.field) || '—',
      badge: chip.badgeField ? (readField(chip.badgeField) || undefined) : undefined,
      tooltip: chip.tooltip,
    }));
    const financeActions = hideActions ? [] : visiblePrimaryActions.map((action) => {
      const done = actionDoneState(action, record, recordScope ?? undefined);
      const disabledReason = !done && actionDisabledReason(action, record, actionRules, recordScope ?? undefined);
      return {
        key: action.key,
        label: done ? done.label : action.label,
        icon: action.icon ? ICONS[action.icon] : undefined,
        enabled: !done && !disabledReason,
        variant: action.tone,
        disabledReason: disabledReason || undefined,
        onClick: done || disabledReason ? undefined : () => fireAction(action.key),
      };
    });
    const steps = rawStatus ? getWorkflowSteps(moduleName, rawStatus) : [];
    return (
      <div className="fin-detail-header">
        <div className="fin-dh__top">
          <div className="fin-dh__identity">
            <FinanceAvatar name={title} size="lg" />
            <div className="fin-dh__who">
              <div className="fin-dh__name-row">
                <h2 className="fin-dh__name">{title || 'Details'}</h2>
                {rawStatus ? <FinanceStatusPill status={rawStatus} /> : null}
              </div>
              {eyebrow ? <span className="fin-dh__designation">{eyebrow}</span> : null}
            </div>
          </div>
          {financeActions.length ? (
            <div className="fin-dh__actions">
              {financeActions.map((action) => {
                const button = (
                  <AppButton
                    key={action.key ?? action.label}
                    variant={action.enabled ? action.variant ?? 'default' : 'default'}
                    icon={action.icon}
                    disabled={!action.enabled}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </AppButton>
                );
                return action.enabled || !action.disabledReason ? button : (
                  <Tooltip title={action.disabledReason} key={action.key ?? action.label}>
                    {/* span wrapper: antd tooltips don't fire on disabled buttons */}
                    <span className="fin-dh__action-wrap">{button}</span>
                  </Tooltip>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Full-width chip row starting at the header's left edge. */}
        {financeChips.length ? (
          <div className="fin-dh__chips">
            {financeChips.map((chip) => <MetaChip chip={chip} key={chip.key ?? chip.label} />)}
          </div>
        ) : null}

        {steps.length ? <FinanceWorkflowSteps steps={steps} /> : null}
      </div>
    );
  }

  return (
    <>
    <Card className="znx-dh-card">
      <div className="znx-dh-top">
        <div className="znx-dh-left">
          {eyebrow && <div className="znx-dh-eyebrow">{eyebrow}</div>}

          <div className="znx-dh-titlerow">
            <span className="znx-dh-title">{title}</span>
              {titleIds.map((item) => (
              <span className="znx-dh-titleid" key={item.label}>
                <span className="znx-dh-id-label">{item.label}:</span> {item.value}
              </span>
            ))}
            {status && (
              // <Tag color={status.color} className="znx-dh-status">
              <Tag
                color={status.color}
                className="znx-dh-status"
                style={{
                  backgroundColor: status.backgroundColor ?? status.color,
                  borderColor: status.backgroundColor ?? status.color,
                  color: status.textColor,
                }}
              >
                {status.label}
              </Tag>
            )}

          
          </div>

          {ids.length > 0 && (
            <div className="znx-dh-ids">
              {ids.map((item) => (
                <span className="znx-dh-id" key={item.label}>
                  <span className="znx-dh-id-label">{item.label}:</span> {item.value}
                </span>
              ))}
            </div>
          )}

          {meta.length > 0 && (
            <div className="znx-dh-meta">
              {/* One meta entry, resolved against THIS record (see
                  ./headerMeta.js): a `labelWhen` rule can re-label it — the
                  same rule shape and matcher the forms use, so a C2C
                  submission's rate reads "Employer rate" here exactly as it
                  does on the form — and `variant:'pill'` renders it through the
                  status pill's styling instead of as body text. */}
              {meta.map((item, index) => {
                const resolved = resolveMetaItem(item, (key) => getNestedValue(record, key) ?? record?.[key]);
                return (
                  <span className="znx-dh-meta-item" key={index}>
                    {resolved.icon && ICONS[resolved.icon] && (
                      <span className="znx-dh-meta-icon">{ICONS[resolved.icon]}</span>
                    )}
                    {resolved.variant === 'pill' ? (
                      <>
                        {resolved.label && <span className="znx-dh-meta-label">{resolved.label}:</span>}
                        <Tag
                          className="znx-dh-meta-pill"
                          color={resolved.tagColor}
                          style={resolved.tagStyle}
                        >
                          {resolved.value || resolved.text}
                        </Tag>
                      </>
                    ) : (
                      <span>{resolved.text}</span>
                    )}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {!hideActions && (
          <div className="znx-dh-actions-col">
            <div className="znx-dh-actions">
              {visiblePrimaryActions.map((action) => {
                const done = actionDoneState(action, record, recordScope ?? undefined);
                const disabledReason = !done && actionDisabledReason(action, record, actionRules, recordScope ?? undefined);
                const button = (
                  <Button
                    key={action.key}
                    type="text"
                    disabled={!!done || !!disabledReason}
                    className={`znx-dh-btn znx-dh-btn--${done ? done.tone : (action.tone ?? 'primary')}${done ? ' znx-dh-btn--done' : ''}`}
                    icon={done ? <CheckCircleOutlined /> : (action.icon ? ICONS[action.icon] : undefined)}
                    onClick={done || disabledReason ? undefined : () => fireAction(action.key)}
                  >
                    {done ? done.label : action.label}
                  </Button>
                );
                return disabledReason
                  // Disabled elements don't reliably fire the mouse events
                  // Tooltip listens for, so give it a hoverable wrapper.
                  ? <Tooltip key={action.key} title={disabledReason}><span>{button}</span></Tooltip>
                  : button;
              })}

              {visibleMoreActions.length > 0 && (
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{ items: moreMenuItems }}
                >
                  <Button
                    type="text"
                    className="znx-dh-more"
                    icon={<MoreOutlined />}
                  />
                </Dropdown>
              )}
            </div>

            {stats.length > 0 && (
              <div className="znx-dh-stats">
                {stats.map((stat) => (
                  <div key={stat.label} className="znx-dh-stat">
                    <span className="znx-dh-stat-value">{stat.value}</span>
                    <span className="znx-dh-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            )}

            {timestamp?.value && (
              <div className="znx-dh-created">
                Created on {timestamp.value}
              </div>
            )}
          </div>
        )}
      </div>

      {visibleTabs.length > 0 && (
        <div className="znx-dh-tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`znx-dh-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </Card>

    <DocumentViewer
      documents={viewerDocs}
      open={viewerOpen}
      onClose={() => setViewerOpen(false)}
    />
    </>
  );
}
